import styled from '@emotion/styled'
import { spacing, fontSize, colors, fontWeight } from '@retailer-platform/shared-components'
import React, { type FunctionComponent } from 'react'
import range from 'lodash/range'
import { useDomainMessages } from 'domains/shifts/utils/domain/intl'
import { intervalDifference } from 'domains/shifts/components/utils/intervalUtils'
import { isStoreClosed } from 'domains/shifts/components/utils/shiftsUtils'
import { type PartnerPickRecurringShiftsFormData } from 'domains/shifts/components/recurring-shifts/recurringShift.types'
import { RecommendedShiftCell } from 'domains/shifts/components/components/recommended-shifts/RecommendedShiftCell'
import { RecommendationTypeEnum } from 'domains/shifts/api'
import { ShiftType, type ShopperWithShifts } from 'domains/shifts/components/types'
import { useRecommendedShiftsContext } from 'domains/shifts/components/contexts/RecommendationShiftsContext'
import { useShiftManagementContext } from 'domains/shifts/components/contexts/ShiftManagementContext'
import { useShiftsStateContext } from 'domains/shifts/components/contexts/ShiftsStateContext'

const Container = styled.div({
  display: 'flex',
  alignItems: 'center',
  height: spacing.X64,
  zIndex: 0,
})

const RowHeader = styled.div({
  height: '100%',
  width: '260px',
  display: 'flex',
  flexShrink: 0,
  alignItems: 'center',
  borderRight: `1px solid ${colors.GRAYSCALE.X20}`,
  background: colors.GRAYSCALE.X10,
  paddingLeft: '30px',
  paddingRight: spacing.X32,
  textTransform: 'uppercase',
  fontSize: fontSize.X12,
  fontWeight: fontWeight.SEMIBOLD,
  color: colors.GRAYSCALE.X50,
})

const Grid = styled.div<{ columns: number }>(({ columns }) => ({
  flexGrow: 1,
  display: 'grid',
  gridTemplateColumns: `repeat(${columns}, 1fr)`,
  gridTemplateRows: spacing.X64,
  background: colors.GRAYSCALE.X20,
  gridGap: '1px',
}))

const BackgroundCell = styled.div<{
  number: number
  isClosed: boolean
}>(({ number, isClosed }) => ({
  width: '100%',
  height: '100%',
  backgroundColor: colors.GRAYSCALE.X10,
  gridRow: '1/1',
  gridColumn: `${number + 1}/${number + 2}`,
  position: 'relative',

  '&:after': isClosed
    ? {
        zIndex: 1,
        content: '""',
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        backgroundColor: colors.GRAYSCALE.X30,
        opacity: '0.4',
      }
    : {},
}))

interface Props {
  shoppers: ShopperWithShifts[]
  formData: PartnerPickRecurringShiftsFormData
  zoneOffset: number
  warehouseId: string
  warehouseLocationId: string
  userAccountId: string
}

export const RecommendedShiftsRow: FunctionComponent<Props> = ({ ...props }) => {
  const {
    storeHours: { regularStoreOpen, regularStoreClose, actualStoreClose, actualStoreOpen },
  } = useShiftManagementContext()
  const numberColumns = intervalDifference(regularStoreOpen, regularStoreClose)

  const backgroundCellRefs = []
  range(numberColumns).forEach(i => (backgroundCellRefs[i] = React.createRef()))

  const messages = useDomainMessages({
    rowTitle: 'shiftsDomain.staffingAlert.recommendedShiftsRowTitle',
  })

  const { recommendations } = useRecommendedShiftsContext()
  const {
    shiftsState: { shiftType },
  } = useShiftsStateContext()

  const isCellActive = recommendationType =>
    (recommendationType === RecommendationTypeEnum.NoRunner && shiftType === ShiftType.Running) ||
    (recommendationType !== RecommendationTypeEnum.NoRunner && shiftType !== ShiftType.Running)

  // We want to make sure the active recommendations (by active tab) always want to be on top
  // The solution here is to make sure the inactive recommendations are pushed to the front of array
  // The active recommendations will then be rendered last and that will be render on top of
  // the inactive recommendations
  if (recommendations.length > 0) {
    recommendations.sort((a, b) =>
      shiftType === ShiftType.Running
        ? a.recommendationType.localeCompare(b.recommendationType)
        : b.recommendationType.localeCompare(a.recommendationType)
    )
  }

  return (
    recommendations.length > 0 && (
      <Container data-testid="shopper-shifts-recommended-shifts-row">
        <RowHeader>{messages.rowTitle}</RowHeader>
        <Grid columns={numberColumns}>
          {range(numberColumns).map(i => (
            <BackgroundCell
              ref={backgroundCellRefs[i]}
              data-testid={`recommended-shifts-row-background-cell-${i}`}
              key={i}
              number={i}
              isClosed={isStoreClosed(i, { actualStoreClose, actualStoreOpen, regularStoreOpen })}
            />
          ))}
          {recommendations.map(({ recommendationType, id, startsAt, endsAt }) => (
            <RecommendedShiftCell
              key={id}
              recommendationId={id}
              type={recommendationType}
              start={new Date(startsAt)}
              end={new Date(endsAt)}
              regularStoreOpen={regularStoreOpen}
              inactive={!isCellActive(recommendationType)}
              {...props}
            />
          ))}
        </Grid>
      </Container>
    )
  )
}
